import { useState } from 'react';
import type { FilterState, InstrumentType, InstrumentStatus, ActId } from '@/types';
import type { TimelineFilterState, TimelinePeriod, TimelineEventType, TimelineVerification } from '@/hooks/useTimelineFilterState';
import styles from './SearchBar.module.css';
import { SemanticPanel, type MandateLabel } from './SemanticPanel';
import type { SemanticState, SemanticResult } from '@/semantic/useSemanticSearch';

interface Props {
  filters: FilterState;
  isActive: boolean;
  onQueryChange: (q: string) => void;
  onToggleHasMandates: () => void;
  onToggleType: (t: InstrumentType) => void;
  onToggleStatus: (s: InstrumentStatus) => void;
  onClearFilters: () => void;
  onCollapseAll: () => void;
  /** Articles matching the active filters (columns view only). */
  matchCount?: number;
  view: 'columns' | 'mandates' | 'timeline';
  onSetView: (v: 'columns' | 'mandates' | 'timeline') => void;
  visibleActs: Set<ActId>;
  onToggleAct: (actId: ActId) => void;
  // Timeline-specific filters
  tlFilters: TimelineFilterState;
  isTimelineFilterActive: boolean;
  onSetPeriod: (p: TimelinePeriod) => void;
  onToggleTimelineType: (t: TimelineEventType) => void;
  onToggleVerification: (v: TimelineVerification) => void;
  onClearTimelineFilters: () => void;
  // Search by meaning (in-browser semantic search)
  semanticOn: boolean;
  onToggleSemantic: () => void;
  semantic: SemanticState;
  mandates: Record<string, MandateLabel | undefined>;
  onOpenSemanticHit: (hit: SemanticResult) => void;
  onOpenSemanticHitTab: (hit: SemanticResult) => void;
}

const ACT_DOTS: { id: ActId; color: string; label: string }[] = [
  { id: 'amld',  color: '#1D4ED8', label: 'AMLD' },
  { id: 'amlr',  color: '#0D9488', label: 'AMLR' },
  { id: 'amlar', color: '#7C3AED', label: 'AMLAR' },
  { id: 'tfr',   color: '#C2410C', label: 'TFR' },
];

const TYPES: InstrumentType[] = ['RTS', 'ITS', 'GL'];

const STATUSES: { value: InstrumentStatus; label: string; color: string }[] = [
  { value: 'adopted',             label: 'Adopted',             color: '#6D28D9' },
  { value: 'final_report',        label: 'Final Report',        color: '#15803D' },
  { value: 'consultation_closed', label: 'Consultation Closed', color: '#1D4ED8' },
  { value: 'consultation_open',   label: 'Open Consultation',   color: '#B45309' },
  { value: 'planned',             label: 'Planned',             color: '#475569' },
];

export function SearchBar({
  filters, isActive, onQueryChange,
  onToggleHasMandates,
  onToggleType, onToggleStatus,
  onClearFilters, onCollapseAll, matchCount,
  view, onSetView,
  visibleActs, onToggleAct,
  tlFilters, isTimelineFilterActive,
  onSetPeriod, onToggleTimelineType, onToggleVerification,
  onClearTimelineFilters,
  semanticOn, onToggleSemantic, semantic, mandates, onOpenSemanticHit, onOpenSemanticHitTab,
}: Props) {
  const [showFilters, setShowFilters] = useState(false);
  const [showTimelineFilters, setShowTimelineFilters] = useState(false);
  const hasTypeOrStatus = filters.types.length > 0 || filters.statuses.length > 0;
  const timelineFilterCount =
    (tlFilters.period !== 'all' ? 1 : 0) +
    (tlFilters.types.length > 0 ? 1 : 0) +
    (tlFilters.verification.length > 0 ? 1 : 0);

  return (
    <div className={styles.wrapper}>
      <div className={styles.bar}>
          {/* Primary view tabs — first thing in the bar: Acts · Mandates · Timeline */}
        <div className={styles.viewTabs} role="group" aria-label="View" data-tour="acts-mandates">
          <button className={`${styles.viewTab} ${view === 'columns' ? styles.viewTabActive : ''}`} onClick={() => view !== 'columns' && onSetView('columns')} aria-pressed={view === 'columns'} aria-label="Acts view">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.viewTabIcon}><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
            Acts
          </button>
          <button className={`${styles.viewTab} ${view === 'mandates' ? styles.viewTabActive : ''}`} onClick={() => view !== 'mandates' && onSetView('mandates')} aria-pressed={view === 'mandates'} aria-label="Mandates view">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.viewTabIcon}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            Mandates
          </button>
          <button className={`${styles.viewTab} ${view === 'timeline' ? styles.viewTabActive : ''}`} onClick={() => view !== 'timeline' && onSetView('timeline')} aria-pressed={view === 'timeline'} aria-label="Timeline view" data-tour="timeline">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.viewTabIcon}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Timeline
          </button>
        </div>

        {(view === 'columns' || view === 'mandates') && (<>
          {/* Search input */}
          <div className={`${styles.inputWrap} ${semanticOn ? styles.inputWrapSemantic : ''}`} data-tour="search">
            <span className={styles.searchIcon}>{semanticOn ? '✦' : '🔍'}</span>
            <input
              type="text"
              className={styles.input}
              placeholder={semanticOn ? 'Describe what you are looking for… (meaning, not keywords)' : 'Search articles, titles, mandates… (/ to focus)'}
              value={filters.query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') onQueryChange(''); if (e.key === 'Enter' && semanticOn) window.dispatchEvent(new CustomEvent('aml:semantic-show')); }}
              onFocus={() => { if (semanticOn) window.dispatchEvent(new CustomEvent('aml:semantic-show')); }}
            />
            {filters.query && (
              <button className={styles.clearInput} onClick={() => onQueryChange('')} aria-label="Clear search">×</button>
            )}
            {matchCount !== undefined && !semanticOn && <span className={styles.matchCount} aria-live="polite">{matchCount} article{matchCount === 1 ? '' : 's'}</span>}
            <button
              className={`${styles.semanticToggle} ${semanticOn ? styles.semanticToggleOn : ''}`}
              onClick={onToggleSemantic}
              aria-pressed={semanticOn}
              data-tour="semantic"
              title={semanticOn ? 'Search by meaning is on — click to go back to keyword search' : 'Search by meaning: describe what you need in your own words. Runs inside your browser (one-time ~40 MB download, then cached).'}
            >
              {semanticOn ? '✦ Meaning' : 'Meaning'}
            </button>
          </div>

          {/* Quick toggles */}
          <div className={styles.quickFilters} data-tour="filters">
            {view === 'columns' && (
              <button
                className={`${styles.filterBtn} ${filters.hasMandates ? styles.active : ''}`}
                onClick={onToggleHasMandates}
              >
                Has mandates
              </button>
            )}
            <button
              className={`${styles.filterBtn} ${showFilters || hasTypeOrStatus ? styles.active : ''}`}
              onClick={() => setShowFilters(v => !v)}
            >
              Filters {hasTypeOrStatus ? `(${filters.types.length + filters.statuses.length})` : ''} ▾
            </button>
            {isActive && (
              <button className={styles.clearBtn} onClick={() => { onClearFilters(); setShowFilters(false); }}>
                ✕ Clear
              </button>
            )}
          </div>
        </>)}

        {view === 'timeline' && (
          <div className={styles.quickFilters} data-tour="tl-filters">
            {([{v: 'future', label: 'Upcoming'}, {v: 'thisYear', label: 'This year'}, {v: 'all', label: 'All'}] as {v: TimelinePeriod; label: string}[]).map(p => (
              <button key={p.v} className={`${styles.chipBtn} ${tlFilters.period === p.v ? styles.chipActive : ''}`} onClick={() => onSetPeriod(p.v)}>{p.label}</button>
            ))}
            <button className={styles.todayBtn} onClick={() => window.dispatchEvent(new CustomEvent('aml:timeline-today'))} title="Scroll to today">Today</button>
            <button
              className={`${styles.filterBtn} ${showTimelineFilters || isTimelineFilterActive ? styles.active : ''}`}
              onClick={() => setShowTimelineFilters(v => !v)}
            >
              Filters {timelineFilterCount > 0 ? `(${timelineFilterCount})` : ''} ▾
            </button>
            {isTimelineFilterActive && (
              <button className={styles.clearBtn} onClick={() => { onClearTimelineFilters(); setShowTimelineFilters(false); }}>
                ✕ Clear
              </button>
            )}
          </div>
        )}

        {/* Right controls */}
        <div className={styles.controls}>
          {/* Focus on (shared across views) */}
          <div className={styles.focusOn} data-tour="focus">
            <span className={styles.focusLabel}>Focus on</span>
            {ACT_DOTS.map(dot => {
              const active = visibleActs.has(dot.id);
              const isLast = active && visibleActs.size === 1;
              return (
                <button
                  key={dot.id}
                  className={styles.dotBtn}
                  style={{
                    background: active ? dot.color : 'transparent',
                    borderColor: dot.color,
                    opacity: isLast ? 0.5 : 1,
                    cursor: isLast ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => !isLast && onToggleAct(dot.id)}
                  title={active ? `Hide ${dot.label}` : `Show ${dot.label}`}
                  aria-label={active ? `Hide ${dot.label}` : `Show ${dot.label}`}
                  aria-pressed={active}
                />
              );
            })}
          </div>

          {view === 'columns' && (
            <button className={styles.controlBtn} onClick={onCollapseAll} title="Collapse all" aria-label="Collapse all expanded items">⊟</button>
          )}
        </div>
      </div>

      {(view === 'columns' || view === 'mandates') && semanticOn && (
        <SemanticPanel state={semantic} query={filters.query} mandates={mandates} onOpen={onOpenSemanticHit} onOpenTab={onOpenSemanticHitTab} onClose={() => { /* hidden until the query changes */ }} />
      )}

      {/* Expanded filter panel — columns view */}
      {(view === 'columns' || view === 'mandates') && showFilters && (
        <div className={styles.filterPanel}>
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Type</span>
            {TYPES.map(t => (
              <button
                key={t}
                className={`${styles.chipBtn} ${filters.types.includes(t) ? styles.chipActive : ''}`}
                onClick={() => onToggleType(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className={styles.filterSep} />
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Status</span>
            {STATUSES.map(s => (
              <button
                key={s.value}
                className={`${styles.chipBtn} ${filters.statuses.includes(s.value) ? styles.chipActive : ''}`}
                style={filters.statuses.includes(s.value) ? {borderColor: s.color, color: s.color, background: s.color+'14'} : {}}
                onClick={() => onToggleStatus(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Expanded filter panel — timeline view */}
      {view === 'timeline' && showTimelineFilters && (
        <div className={styles.filterPanel}>
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Period</span>
            {([
              {v: 'all', label: 'All'},
              {v: 'past', label: 'Past'},
              {v: 'future', label: 'Future'},
              {v: 'thisYear', label: 'This year'},
            ] as {v: TimelinePeriod; label: string}[]).map(p => (
              <button
                key={p.v}
                className={`${styles.chipBtn} ${tlFilters.period === p.v ? styles.chipActive : ''}`}
                onClick={() => onSetPeriod(p.v)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className={styles.filterSep} />
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Type</span>
            {([
              {v: 'milestone', label: 'Milestone', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE'},
              {v: 'deadline',  label: 'Deadline',  bg: '#FEF3C7', color: '#B45309', border: '#FDE68A'},
              {v: 'hearing',   label: 'Hearing',   bg: '#F3E8FF', color: '#6D28D9', border: '#DDD6FE'},
            ] as {v: TimelineEventType; label: string; bg: string; color: string; border: string}[]).map(t => {
              const active = tlFilters.types.includes(t.v);
              return (
                <button
                  key={t.v}
                  className={`${styles.chipBtn} ${active ? styles.chipActive : ''}`}
                  style={active ? {background: t.bg, color: t.color, borderColor: t.border} : {}}
                  onClick={() => onToggleTimelineType(t.v)}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className={styles.filterSep} />
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>Verification</span>
            {([
              {v: 'confirmed',   label: '✓ Confirmed',         bg: '#F0FDF4', color: '#15803D', border: '#86EFAC'},
              {v: 'unconfirmed', label: '⚠ Not yet confirmed', bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA'},
            ] as {v: TimelineVerification; label: string; bg: string; color: string; border: string}[]).map(v => {
              const active = tlFilters.verification.includes(v.v);
              return (
                <button
                  key={v.v}
                  className={`${styles.chipBtn} ${active ? styles.chipActive : ''}`}
                  style={active ? {background: v.bg, color: v.color, borderColor: v.border} : {}}
                  onClick={() => onToggleVerification(v.v)}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
